import React, { useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Form, Input, Button, Space } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { ChevronLeft } from "lucide-react";
// Full-bleed background (office photo + logo + cream panel already baked in)
import backgroundImage from "../../assets/images/forgotpassword.png";
import httpInjectorService from "../../services/http-injector.service";
import { NotificationContext } from "../../NotificationProvider";

const NAVY = "#1b2a63";
const ORANGE = "#f39c2d";
const CREAM = "#faf6ec";

// Small decorative network + payroll doc illustration, matches brand colors
const HeaderIllustration = () => (
  <svg
    viewBox="0 0 420 200"
    width="100%"
    height="150"
    style={{ maxWidth: 420 }}
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* connecting lines */}
    <g stroke={NAVY} strokeWidth="1.5" opacity="0.6">
      <line x1="60" y1="130" x2="150" y2="90" />
      <line x1="150" y1="90" x2="90" y2="50" />
      <line x1="150" y1="90" x2="230" y2="60" />
      <line x1="230" y1="60" x2="290" y2="20" />
      <line x1="150" y1="90" x2="180" y2="150" />
      <line x1="230" y1="60" x2="270" y2="110" />
    </g>
    {/* nodes */}
    <circle cx="60" cy="130" r="6" fill={NAVY} />
    <circle cx="90" cy="50" r="6" fill={ORANGE} />
    <circle cx="150" cy="90" r="12" fill={ORANGE} opacity="0.25" />
    <circle cx="150" cy="90" r="7" fill={NAVY} />
    <circle cx="180" cy="150" r="6" fill={NAVY} />
    <circle cx="230" cy="60" r="6" fill={NAVY} />
    <circle cx="270" cy="110" r="6" fill={ORANGE} />
    <circle cx="290" cy="20" r="6" fill={NAVY} />

    {/* bar chart */}
    <g>
      <rect x="30" y="150" width="10" height="20" fill={NAVY} />
      <rect x="45" y="140" width="10" height="30" fill={ORANGE} />
      <rect x="60" y="130" width="10" height="40" fill={NAVY} />
      <path
        d="M28 130 L48 115 L63 122 L85 95"
        stroke={ORANGE}
        strokeWidth="2.5"
        fill="none"
      />
    </g>

    {/* pie */}
    <circle cx="205" cy="130" r="16" fill={ORANGE} opacity="0.3" />
    <path
      d="M205 114 A16 16 0 0 1 219 138 L205 130 Z"
      fill={NAVY}
    />

    {/* payroll doc card */}
    <g transform="translate(300,70)">
      <rect
        x="0"
        y="0"
        width="90"
        height="110"
        rx="8"
        fill="#fff"
        stroke={NAVY}
        strokeWidth="2"
      />
      <rect x="0" y="0" width="90" height="26" rx="8" fill={NAVY} />
      <text
        x="45"
        y="17"
        textAnchor="middle"
        fontSize="11"
        fontWeight="700"
        fill="#fff"
        fontFamily="sans-serif"
      >
        PAYROLL
      </text>
      <rect x="10" y="38" width="70" height="8" rx="2" fill="#e8e8e8" />
      <rect x="10" y="52" width="45" height="6" rx="2" fill="#e8e8e8" />
      <rect x="10" y="64" width="45" height="6" rx="2" fill="#e8e8e8" />
      <rect x="10" y="76" width="45" height="6" rx="2" fill="#e8e8e8" />
      <rect x="10" y="92" width="34" height="10" rx="5" fill={ORANGE} />
    </g>

    {/* small dollar tag top right */}
    <g transform="translate(380,45)">
      <circle r="14" fill="#fff" stroke={ORANGE} strokeWidth="2" />
      <text
        textAnchor="middle"
        dy="5"
        fontSize="13"
        fontWeight="700"
        fill={ORANGE}
        fontFamily="sans-serif"
      >
        $
      </text>
    </g>
  </svg>
);

const Forgotpassword = ({ onPasswordResetSuccess }) => {
  const { Title, Text } = Typography;
  const navigate = useNavigate();
  const { openNotification } = useContext(NotificationContext);

  const [isOTPSent, setIsOTPSent] = useState(false);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const inputRefs = useRef([]);

  const handleOTPChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleBackspace = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const onSendOTP = async ({ email }) => {
    if (otpLoading) return;
    setOtpLoading(true);
    try {
      const response = await httpInjectorService.sendOTP({ email });
      if (response.status === "SUCCESS") {
        openNotification("success", response.message);
        setIsOTPSent(true);
        setEmail(email);
      } else {
        openNotification("error", response.message);
      }
    } catch (error) {
      console.error("OTP send failed", error);
      openNotification("error", "Something went wrong while sending OTP.");
    } finally {
      setOtpLoading(false);
    }
  };

  const onResetPassword = async ({ password }) => {
    const otpCode = otp.join("");
    if (otpCode.length !== 4) {
      openNotification("error", "OTP must be 4 digits");
      return;
    }

    if (resetLoading) return;
    setResetLoading(true);
    try {
      const payload = {
        email,
        email_otp: otpCode,
        new_password: password,
      };
      const response = await httpInjectorService.forgotPassword(payload);
      if (response.status === "SUCCESS") {
        openNotification("success", response.message);
        onPasswordResetSuccess();
      } else {
        openNotification("error", response.message);
      }
    } catch (error) {
      console.error("Password reset failed", error);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        backgroundColor: CREAM,
      }}
    >
      {/* Full-bleed background: office photo + logo + cream panel already baked into this image */}
      <img
        src={backgroundImage}
        alt=""
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 0,
        }}
      />

      {/* Content card, overlaid on the cream right half of the background */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          marginLeft: "auto",
          width: "50%",
          minHeight: "100vh",
          padding: "40px 60px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* top row: back link + phone */}
     

        {/* illustration */}
        <div style={{ marginTop: 24 }}>
          <HeaderIllustration />
        </div>

        {/* heading */}
        <Title level={2} style={{ color: NAVY, marginTop: 12, marginBottom: 24 }}>
          {isOTPSent ? "Enter OTP & new password" : "Reset your password"}
        </Title>

        {/* form */}
        <Form
          onFinish={isOTPSent ? onResetPassword : onSendOTP}
          layout="vertical"
          style={{ maxWidth: 440 }}
        >
          {!isOTPSent ? (
            <>
              <Form.Item
                label="Email address"
                name="email"
                rules={[
                  { required: true, message: "Please input your email!" },
                  {
                    pattern:
                      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: "Please enter a valid email address!",
                  },
                ]}
              >
                <Input placeholder="Email" size="large" bordered={false} style={{ borderBottom: "1px solid #ccc", borderRadius: 0, paddingLeft: 0 }} />
              </Form.Item>

              <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
                Wait! If you need further assistance, please contact our support team.
              </Text>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  disabled={otpLoading}
                  style={{
                    width: "100%",
                    backgroundColor: NAVY,
                    borderColor: NAVY,
                    borderRadius: 8,
                    letterSpacing: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                  }}
                >
                  {otpLoading ? (
                    <>
                      <LoadingOutlined style={{ fontSize: 16 }} spin />
                      <span>SENDING OTP...</span>
                    </>
                  ) : (
                    <span>SEND OTP</span>
                  )}
                </Button>
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item label="OTP">
                <Space>
                  {[0, 1, 2, 3].map((index) => (
                    <Input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      value={otp[index]}
                      onChange={(e) => handleOTPChange(index, e.target.value)}
                      onKeyDown={(e) => handleBackspace(index, e)}
                      maxLength={1}
                      size="large"
                      style={{
                        width: 50,
                        textAlign: "center",
                        fontSize: "20px",
                      }}
                    />
                  ))}
                </Space>
              </Form.Item>

              <Form.Item
                label="New Password"
                name="password"
                rules={[
                  { required: true, message: "Please enter your password!" },
                  { min: 6, message: "Password must be at least 6 characters" },
                ]}
              >
                <Input.Password placeholder="New Password" size="large" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  disabled={resetLoading}
                  style={{
                    width: "100%",
                    backgroundColor: NAVY,
                    borderColor: NAVY,
                    borderRadius: 8,
                    letterSpacing: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                  }}
                >
                  {resetLoading ? (
                    <>
                      <LoadingOutlined style={{ fontSize: 16 }} spin />
                      <span>RESETTING PASSWORD...</span>
                    </>
                  ) : (
                    <span>CHANGE PASSWORD</span>
                  )}
                </Button>
              </Form.Item>
            </>
          )}
        </Form>
      </div>
    </div>
  );
};

export default Forgotpassword;
